import { FormCategory } from '../enums/form-category.enum';
import { formCategoriesList, FORMS_BASE_PATH } from '../form-categories-list';
import { RoleRight } from '../../interfaces';
import { UserRoleScope } from '../../user-api/enums/user-role-scope.enum';
import { UserRole } from '../../user-api/types/user-role.type';

/**
 * Type guard that checks if the given value is a known form category.
 *
 * @param value - Value to check, e. g. a `category` database value or URL segment
 */
export function isFormCategory(value: unknown): value is FormCategory {
  return typeof value === 'string' && (Object.values(FormCategory) as string[]).includes(value);
}

/**
 * Build the frontend path of a form category list, e. g. `forms/instances/manualMarketData`.
 *
 * @param category - Form category
 */
export function getFormCategoryPath(category: FormCategory): string {
  return `${FORMS_BASE_PATH}/${category}`;
}

/**
 * Get the form categories that the given scopes grant access to.
 * Unknown scopes and scopes that do not belong to forms are ignored.
 *
 * @param scopes - Scopes of a role assignment (`null`/`undefined` = no scopes)
 * @returns Form categories in display order
 */
export function getFormCategoriesOfScopes(scopes: readonly string[] | null | undefined): FormCategory[] {
  if (!Array.isArray(scopes) || scopes.length === 0) {
    return [];
  }

  return formCategoriesList
    .filter((formCategory) => scopes.includes(formCategory.scope))
    .map((formCategory) => formCategory.category);
}

/**
 * Get the form categories that a single role assignment grants access to with the given right.
 *
 * The assignment must hold the `right` AND the scope of a category (fail-closed: assignments
 * without scopes grant nothing). This is the single definition of the rule used by the frontend
 * (menu, redirects) and the backend (list filter).
 *
 * @param roleAssignment - Role assignment of the user (rights and scopes are evaluated)
 * @param right - Required right, e. g. `FORMINSTANCE_READ`
 * @returns Form categories in display order (empty if the assignment grants nothing)
 */
export function getFormCategoriesOfRoleAssignment(
  roleAssignment: Pick<UserRole, 'rights' | 'scopes'>,
  right: RoleRight,
): FormCategory[] {
  if (!roleAssignment.rights.includes(right)) {
    return [];
  }

  return getFormCategoriesOfScopes(roleAssignment.scopes);
}

/**
 * Get the scope that grants access to the given form category (each category belongs to exactly one scope).
 *
 * @param category - Form category
 * @returns Scope or `undefined` if the category is not configured
 */
export function getScopeOfFormCategory(category: FormCategory): UserRoleScope | undefined {
  return formCategoriesList.find((formCategory) => formCategory.category === category)?.scope;
}
